import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import PortalLayout from '../PortalLayout';
import { payrunService, payrollConfigService } from '../../../services/api';
import { useAuth } from '../../../context/AuthContext';
import { useLanguage } from '../../../context/LanguageContext';
import useIsMobile from '../../../hooks/useIsMobile';
import MobileCard from '../../../components/common/MobileCard';
import MobileHero from '../../../components/common/MobileHero';
import MobileBanner from '../../../components/common/MobileBanner';
import { C } from '../../../styles/theme';

// ─── Tokens ──────────────────────────────────────────────────────────────────

const FONT = "'Plus Jakarta Sans', sans-serif";

// ─── Helpers ─────────────────────────────────────────────────────────────────

const fmtMoney = (currency, amount) => `${currency}${Number(amount || 0).toFixed(2)}`;

const fmtDate = (iso) => {
    if (!iso) return '—';
    const d = new Date(iso);
    if (isNaN(d.getTime())) return '—';
    return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
};

const fmtPeriod = (start, end) => `${fmtDate(start)} – ${fmtDate(end)}`;

const URGENCY_STYLES = {
    red: { dot: C.red, bg: C.redLight, border: C.redBorder },
    amber: { dot: C.amber, bg: C.amberLight, border: C.amberBorder },
    green: { dot: C.green, bg: C.greenLight, border: '#bfdbfe' },
};

const STATUS_STYLES = {
    draft: { color: C.slate, bg: C.slateLight },
    ready: { color: C.amber, bg: C.amberLight },
    partially_paid: { color: C.amber, bg: C.amberLight },
    paid: { color: C.blue, bg: C.blueLight },
    closed: { color: C.green, bg: C.greenLight },
};

const CADENCE_LABEL_KEYS = { daily: 'daily', weekly: 'weekly', monthly: 'monthly' };
const STATUS_LABEL_KEYS = { draft: 'status_draft', ready: 'status_ready', partially_paid: 'status_partially_paid', paid: 'status_paid', closed: 'status_closed' };

// ─── StatusPill ──────────────────────────────────────────────────────────────

const StatusPill = ({ status, t }) => {
    const style = STATUS_STYLES[status] || { color: C.muted, bg: C.slateLight };
    return (
        <span style={{ fontSize: 11, fontWeight: 700, borderRadius: 5, padding: '3px 9px', background: style.bg, color: style.color, textTransform: 'uppercase', letterSpacing: 0.5 }}>
            {t(STATUS_LABEL_KEYS[status] || status)}
        </span>
    );
};

// ─── To Run Widget ───────────────────────────────────────────────────────────

const ToRunCard = ({ run, currency, onOpen, t }) => {
    const u = URGENCY_STYLES[run.urgency] || URGENCY_STYLES.green;
    return (
        <div onClick={() => onOpen(run.pay_run_id)}
            style={{ flex: '1 1 260px', minWidth: 260, background: C.white, border: `1px solid ${u.border}`, borderRadius: 12, padding: 16, cursor: 'pointer', transition: 'transform 0.15s, box-shadow 0.15s' }}
            onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-1px)'; e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.06)'; }}
            onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = 'none'; }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
                <span style={{ width: 9, height: 9, borderRadius: '50%', background: u.dot, flexShrink: 0 }} />
                <span style={{ fontSize: 11, fontWeight: 700, color: C.muted, textTransform: 'uppercase', letterSpacing: 0.8 }}>
                    {t(CADENCE_LABEL_KEYS[run.cadence] || run.cadence)}
                </span>
                <div style={{ marginLeft: 'auto' }}><StatusPill status={run.status} t={t} /></div>
            </div>
            <div style={{ fontSize: 14, fontWeight: 700, color: C.text, marginBottom: 4 }}>
                {fmtPeriod(run.period_start, run.period_end)}
            </div>
            <div style={{ fontSize: 12, color: C.muted, marginBottom: 14 }}>
                {t('pay_date')}: {fmtDate(run.pay_date)}
            </div>
            <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 12 }}>
                <div>
                    <div style={{ fontSize: 10, fontWeight: 700, color: C.mutedLight, textTransform: 'uppercase', letterSpacing: 0.8 }}>{t('unpaid')}</div>
                    <div style={{ fontSize: 18, fontWeight: 800, color: C.text }}>{run.unpaid_count}</div>
                </div>
                <div style={{ textAlign: 'right' }}>
                    <div style={{ fontSize: 10, fontWeight: 700, color: C.mutedLight, textTransform: 'uppercase', letterSpacing: 0.8 }}>{t('owed')}</div>
                    <div style={{ fontSize: 18, fontWeight: 800, color: C.text }}>{fmtMoney(currency, run.total_owed)}</div>
                </div>
            </div>
            <button onClick={(e) => { e.stopPropagation(); onOpen(run.pay_run_id); }}
                style={{ width: '100%', background: C.green, color: C.white, border: 'none', borderRadius: 7, padding: '8px', fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}>
                {run.status === 'draft' ? t('finalize_run') : t('record_payments')}
            </button>
        </div>
    );
};

const ToRunWidget = ({ toRun, currency, onOpen, t }) => {
    if (!toRun.length) {
        return (
            <div style={{ background: C.white, border: `1px solid ${C.border}`, borderRadius: 12, padding: 32, textAlign: 'center', color: C.muted }}>
                <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" style={{ marginBottom: 10, opacity: 0.4 }}>
                    <path d="M9 11l3 3L22 4"/><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"/>
                </svg>
                <div style={{ fontSize: 14, fontWeight: 600, color: C.textLight }}>{t('no_runs_need_attention')}</div>
                <div style={{ fontSize: 12, marginTop: 4 }}>{t('all_caught_up')}</div>
            </div>
        );
    }
    return (
        <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap' }}>
            {toRun.map(run => <ToRunCard key={run.pay_run_id} run={run} currency={currency} onOpen={onOpen} t={t} />)}
        </div>
    );
};

// ─── Status tone mapping for MobileCard ──────────────────────────────────────

const STATUS_TONE = { draft: 'gray', ready: 'amber', partially_paid: 'amber', paid: 'green', closed: 'green' };

// ─── Mobile Run Card ─────────────────────────────────────────────────────────

const MobileRunRow = ({ run, currency, onOpen, t }) => {
    const tone = STATUS_TONE[run.status] || 'gray';
    const accent = run.status === 'draft' ? 'amber' : (run.status === 'paid' || run.status === 'closed') ? 'green' : 'amber';
    const detailRow = (label, value) => (
        <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', fontSize: 13 }}>
            <span style={{ color: C.muted }}>{label}</span>
            <span style={{ fontWeight: 600, color: C.text, textAlign: 'right' }}>{value}</span>
        </div>
    );
    return (
        <div onClick={() => onOpen(run.pay_run_id)} style={{ cursor: 'pointer' }}>
            <MobileCard
                accent={accent}
                title={fmtPeriod(run.period_start, run.period_end)}
                amount={fmtMoney(currency, run.total_net)}
                status={{ label: t(STATUS_LABEL_KEYS[run.status] || run.status), tone }}
                subtitle={`${t(CADENCE_LABEL_KEYS[run.cadence] || run.cadence)} · ${t('pay_date')} ${fmtDate(run.pay_date)}`}
            >
                {detailRow(t('gross'), fmtMoney(currency, run.total_gross))}
                {detailRow(t('net'), fmtMoney(currency, run.total_net))}
                {detailRow(t('paid'), fmtMoney(currency, run.total_paid))}
            </MobileCard>
        </div>
    );
};

// ─── Runs Table ──────────────────────────────────────────────────────────────

const RunsTable = ({ runs, currency, onOpen, t, isMobile }) => {
    if (!runs.length) {
        return (
            <div style={{ padding: 48, textAlign: 'center', color: C.mutedLight, fontSize: 13 }}>
                {t('no_runs_match_filters')}
            </div>
        );
    }
    if (isMobile) {
        return (
            <div style={{ padding: 12 }}>
                {runs.map(r => <MobileRunRow key={r.pay_run_id} run={r} currency={currency} onOpen={onOpen} t={t} />)}
            </div>
        );
    }
    return (
        <div className="portal-table-wrap">
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
                <tr style={{ borderBottom: `1px solid ${C.borderLight}` }}>
                    {[t('period'), t('cadence'), t('status'), t('gross'), t('net'), t('paid'), ''].map((h, i) => (
                        <th key={i} style={{ padding: '12px 16px', textAlign: i >= 3 && i <= 5 ? 'right' : 'left', fontSize: 11, fontWeight: 700, color: C.mutedLight, textTransform: 'uppercase', letterSpacing: 0.8, whiteSpace: 'nowrap' }}>
                            {h}
                        </th>
                    ))}
                </tr>
            </thead>
            <tbody>
                {runs.map(r => (
                    <tr key={r.pay_run_id}
                        onClick={() => onOpen(r.pay_run_id)}
                        style={{ borderBottom: `1px solid ${C.bg}`, cursor: 'pointer', transition: 'background 0.1s' }}
                        onMouseEnter={e => { e.currentTarget.style.background = C.bg; }}
                        onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}>
                        <td style={{ padding: '12px 16px' }}>
                            <div style={{ fontSize: 13, fontWeight: 600, color: C.text }}>{fmtPeriod(r.period_start, r.period_end)}</div>
                            <div style={{ fontSize: 11, color: C.muted, marginTop: 2 }}>{t('pay_date')}: {fmtDate(r.pay_date)}</div>
                        </td>
                        <td style={{ padding: '12px 16px', fontSize: 13, color: C.textLight, textTransform: 'capitalize' }}>
                            {t(CADENCE_LABEL_KEYS[r.cadence] || r.cadence)}
                        </td>
                        <td style={{ padding: '12px 16px' }}><StatusPill status={r.status} t={t} /></td>
                        <td style={{ padding: '12px 16px', fontSize: 13, color: C.textLight, textAlign: 'right' }}>{fmtMoney(currency, r.total_gross)}</td>
                        <td style={{ padding: '12px 16px', fontSize: 13, color: C.textLight, textAlign: 'right' }}>{fmtMoney(currency, r.total_net)}</td>
                        <td style={{ padding: '12px 16px', fontSize: 13, color: C.textLight, textAlign: 'right' }}>{fmtMoney(currency, r.total_paid)}</td>
                        <td style={{ padding: '12px 16px', textAlign: 'right' }}>
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={C.mutedLight} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <polyline points="9 18 15 12 9 6"/>
                            </svg>
                        </td>
                    </tr>
                ))}
            </tbody>
        </table>
        </div>
    );
};

// ─── Create Run Modal ────────────────────────────────────────────────────────

const inputStyle = { width: '100%', border: `1px solid ${C.border}`, borderRadius: 7, padding: '9px 12px', fontSize: 13, color: C.text, outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit' };
const labelStyle = { fontSize: 11, fontWeight: 700, color: C.muted, textTransform: 'uppercase', letterSpacing: 0.6, display: 'block', marginBottom: 5 };

const calcPeriod = (cadence, cfg) => {
    const now = new Date();
    const pad = (n) => String(n).padStart(2, '0');
    const iso = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
    const addDays = (d, n) => { const r = new Date(d); r.setDate(r.getDate() + n); return r; };

    if (cadence === 'monthly') {
        const cutoff = cfg?.monthly_cutoff_day || 31;
        const offset = cfg?.monthly_pay_offset_days || 0;
        const y = now.getFullYear(), m = now.getMonth();
        const lastDay = new Date(y, m + 1, 0).getDate();
        const endDay = Math.min(cutoff, lastDay);
        const end = new Date(y, m, endDay);
        const start = new Date(end);
        start.setMonth(start.getMonth() - 1);
        start.setDate(start.getDate() + 1);
        const payDay = Math.min(endDay + offset, lastDay);
        const payDate = new Date(y, m, payDay);
        return { period_start: iso(start), period_end: iso(end), pay_date: iso(payDate) };
    }
    if (cadence === 'weekly') {
        const cutoffDow = cfg?.weekly_cutoff_day_of_week || 7;
        const offset = cfg?.weekly_pay_offset_days || 1;
        const currentDow = now.getDay() || 7;
        const diff = cutoffDow - currentDow;
        const end = addDays(now, diff <= 0 ? diff : diff - 7);
        const start = addDays(end, -6);
        return { period_start: iso(start), period_end: iso(end), pay_date: iso(addDays(end, offset)) };
    }
    return { period_start: iso(now), period_end: iso(now), pay_date: iso(now) };
};

const CreateRunModal = ({ onSave, onClose, saving, serverError, t }) => {
    const [cfg, setCfg] = useState(null);
    const [form, setForm] = useState({ cadence: 'monthly', period_start: '', period_end: '', pay_date: '' });
    const [error, setError] = useState('');

    useEffect(() => {
        payrollConfigService.get().then(data => {
            setCfg(data);
            const dates = calcPeriod('monthly', data);
            setForm(f => ({ ...f, ...dates }));
        }).catch(() => {});
    }, []);

    const set = (field, value) => {
        if (field === 'cadence' && cfg) {
            const dates = calcPeriod(value, cfg);
            setForm(f => ({ ...f, cadence: value, ...dates }));
        } else {
            setForm(f => ({ ...f, [field]: value }));
        }
    };

    const handleSubmit = () => {
        if (!form.period_start || !form.period_end) return setError(t('dates_required'));
        if (form.period_end < form.period_start) return setError(t('end_after_start'));
        setError('');
        onSave({
            cadence: form.cadence,
            period_start: form.period_start,
            period_end: form.period_end,
            pay_date: form.pay_date || null,
        });
    };

    return (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}
            onClick={e => e.target === e.currentTarget && onClose()}>
            <div className="portal-modal-card" style={{ background: C.white, borderRadius: 14, width: 480, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}
                onKeyDown={e => e.key === 'Escape' && onClose()}>
                <div style={{ padding: '18px 24px', borderBottom: `1px solid ${C.borderLight}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <span style={{ fontSize: 15, fontWeight: 700, color: C.text }}>{t('start_new_run')}</span>
                    <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.mutedLight, padding: 4, display: 'flex' }}>
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                        </svg>
                    </button>
                </div>
                <div style={{ padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: 14 }}>
                    {(error || serverError) && (
                        <div style={{ background: C.redLight, border: `1px solid ${C.redBorder}`, borderRadius: 7, padding: '10px 14px', fontSize: 13, color: '#e11d48' }}>
                            {error || serverError}
                        </div>
                    )}
                    <div>
                        <label style={labelStyle}>{t('cadence')}</label>
                        <select style={inputStyle} value={form.cadence} onChange={e => set('cadence', e.target.value)}>
                            <option value="daily">{t('daily')}</option>
                            <option value="weekly">{t('weekly')}</option>
                            <option value="monthly">{t('monthly')}</option>
                        </select>
                    </div>
                    {form.period_start && (
                        <div style={{ background: C.bg, borderRadius: 8, padding: '14px 16px', display: 'flex', gap: 24 }}>
                            <div>
                                <div style={{ fontSize: 10, fontWeight: 700, color: C.mutedLight, textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 4 }}>{t('period')}</div>
                                <div style={{ fontSize: 14, fontWeight: 700, color: C.text }}>{fmtPeriod(form.period_start, form.period_end)}</div>
                            </div>
                            <div>
                                <div style={{ fontSize: 10, fontWeight: 700, color: C.mutedLight, textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 4 }}>{t('pay_date')}</div>
                                <div style={{ fontSize: 14, fontWeight: 700, color: C.text }}>{fmtDate(form.pay_date)}</div>
                            </div>
                        </div>
                    )}
                </div>
                <div style={{ padding: '14px 24px', borderTop: `1px solid ${C.borderLight}`, display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
                    <button onClick={onClose} style={{ background: C.white, border: `1px solid ${C.border}`, borderRadius: 7, padding: '9px 18px', fontSize: 13, fontWeight: 600, color: C.textLight, cursor: 'pointer', fontFamily: 'inherit' }}>
                        {t('cancel')}
                    </button>
                    <button onClick={handleSubmit} disabled={saving}
                        style={{ background: C.green, color: C.white, border: 'none', borderRadius: 7, padding: '9px 22px', fontSize: 13, fontWeight: 600, cursor: saving ? 'default' : 'pointer', opacity: saving ? 0.7 : 1, fontFamily: 'inherit' }}>
                        {saving ? t('saving') : t('create')}
                    </button>
                </div>
            </div>
        </div>
    );
};

// ─── Main Page ───────────────────────────────────────────────────────────────

const CADENCE_FILTERS = [
    { key: 'all', labelKey: 'all' },
    { key: 'daily', labelKey: 'daily' },
    { key: 'weekly', labelKey: 'weekly' },
    { key: 'monthly', labelKey: 'monthly' },
];

const STATUS_FILTERS = ['all', 'draft', 'ready', 'paid', 'closed'];

const PortalPayRunPage = () => {
    const navigate = useNavigate();
    const { getCurrency } = useAuth();
    const { t } = useLanguage();
    const isMobile = useIsMobile();
    const currency = getCurrency();

    const [toRun, setToRun] = useState([]);
    const [runs, setRuns] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    const [cadenceFilter, setCadenceFilter] = useState('all');
    const [statusFilter, setStatusFilter] = useState('all');

    const [showCreate, setShowCreate] = useState(false);
    const [creating, setCreating] = useState(false);
    const [createError, setCreateError] = useState('');

    const load = useCallback(async () => {
        try {
            setLoading(true);
            setError('');
            const params = {};
            if (cadenceFilter !== 'all') params.cadence = cadenceFilter;
            if (statusFilter !== 'all') params.status = statusFilter;
            const data = await payrunService.list(params);
            setToRun(Array.isArray(data?.to_run) ? data.to_run : []);
            setRuns(Array.isArray(data?.runs) ? data.runs : []);
        } catch {
            setError(t('failed_to_load_pay_runs'));
        } finally {
            setLoading(false);
        }
    }, [cadenceFilter, statusFilter, t]);

    useEffect(() => { load(); }, [load]);

    const openRun = (id) => navigate(`/portal/payrun/${id}`);

    const handleCreate = async (payload) => {
        try {
            setCreating(true);
            setCreateError('');
            const run = await payrunService.createDraft(payload);
            setShowCreate(false);
            if (run?.pay_run_id) navigate(`/portal/payrun/${run.pay_run_id}`);
            else await load();
        } catch (err) {
            setCreateError(err?.response?.data?.detail || t('failed_to_create_run'));
        } finally {
            setCreating(false);
        }
    };

    const chipStyle = (active) => ({
        fontSize: 12, fontWeight: 600, borderRadius: 6, padding: '5px 12px', cursor: 'pointer', border: '1px solid',
        background: active ? C.green : C.white, color: active ? C.white : C.textLight, borderColor: active ? C.green : C.border,
        transition: 'all 0.15s', fontFamily: 'inherit',
    });

    return (
        <PortalLayout>
            <div style={{ fontFamily: FONT, display: 'flex', flexDirection: 'column', gap: 0, height: '100%' }}>

                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
                    <div>
                        <h2 style={{ margin: 0, fontSize: 20, fontWeight: 800, color: C.text }}>{t('pay_run')}</h2>
                        <p style={{ margin: '3px 0 0', fontSize: 13, color: C.muted }}>{t('pay_run_subtitle')}</p>
                    </div>
                    <button onClick={() => { setCreateError(''); setShowCreate(true); }}
                        style={{ display: 'flex', alignItems: 'center', gap: 7, background: C.green, color: C.white, border: 'none', borderRadius: 8, padding: '9px 18px', fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}
                        onMouseEnter={e => e.currentTarget.style.background = C.greenDark} onMouseLeave={e => e.currentTarget.style.background = C.green}>
                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
                        {t('start_new_run')}
                    </button>
                </div>

                {error && <div style={{ background: C.redLight, border: `1px solid ${C.redBorder}`, borderRadius: 8, padding: '10px 14px', fontSize: 13, color: '#e11d48', marginBottom: 16 }}>{error}</div>}

                {/* Mobile hero + banner */}
                {isMobile && !loading && (() => {
                    const totalOwed = toRun.reduce((s, r) => s + Number(r.total_owed || 0), 0);
                    const unpaidPeople = toRun.reduce((s, r) => s + Number(r.unpaid_count || 0), 0);
                    return (
                        <>
                            <MobileHero
                                label={t('owed')}
                                value={fmtMoney(currency, totalOwed)}
                                alert={totalOwed > 0}
                                meta={`${toRun.length} ${toRun.length !== 1 ? (t('runs') || 'runs') : (t('run') || 'run')} ${t('to_run')}`}
                                stats={[
                                    { label: t('to_run'), value: `${toRun.length}` },
                                    { label: t('unpaid'), value: `${unpaidPeople}`, tone: unpaidPeople > 0 ? 'red' : undefined },
                                    { label: t('runs'), value: `${runs.length}` },
                                ]}
                            />
                            {toRun.length > 0 ? (
                                <MobileBanner
                                    tone="warn"
                                    icon={<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" /></svg>}
                                    title={`${toRun.length} ${toRun.length !== 1 ? (t('runs') || 'runs') : (t('run') || 'run')} ${t('need_review') || 'need attention'}`}
                                    subtitle={`${unpaidPeople} ${t('unpaid')} · ${fmtMoney(currency, totalOwed)} ${t('owed')}`}
                                />
                            ) : (
                                <MobileBanner
                                    tone="good"
                                    icon={<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" /><polyline points="22 4 12 14.01 9 11.01" /></svg>}
                                    title={t('all_caught_up')}
                                    subtitle={t('no_runs_need_attention')}
                                />
                            )}
                        </>
                    );
                })()}

                {/* To Run widget */}
                <div style={{ marginBottom: 24 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                        <h3 style={{ margin: 0, fontSize: 14, fontWeight: 700, color: C.text, textTransform: 'uppercase', letterSpacing: 0.8 }}>{t('to_run')}</h3>
                        {toRun.length > 0 && (
                            <span style={{ fontSize: 11, fontWeight: 700, borderRadius: 5, padding: '2px 7px', background: C.greenLight, color: C.green }}>
                                {toRun.length}
                            </span>
                        )}
                    </div>
                    {loading ? (
                        <div style={{ padding: 24, color: C.mutedLight, fontSize: 13 }}>{t('loading')}</div>
                    ) : (
                        <ToRunWidget toRun={toRun} currency={currency} onOpen={openRun} t={t} />
                    )}
                </div>

                {/* Runs table section */}
                <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12, flexWrap: 'wrap', gap: 12 }}>
                        <h3 style={{ margin: 0, fontSize: 14, fontWeight: 700, color: C.text, textTransform: 'uppercase', letterSpacing: 0.8 }}>{t('runs')}</h3>
                        <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
                            <div style={{ display: 'flex', gap: 6 }}>
                                {CADENCE_FILTERS.map(f => (
                                    <button key={f.key} onClick={() => setCadenceFilter(f.key)} style={chipStyle(cadenceFilter === f.key)}>
                                        {t(f.labelKey)}
                                    </button>
                                ))}
                            </div>
                            <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}
                                style={{ ...inputStyle, width: 'auto', padding: '6px 10px', fontSize: 12 }}>
                                {STATUS_FILTERS.map(s => (
                                    <option key={s} value={s}>{s === 'all' ? t('all_statuses') : t(STATUS_LABEL_KEYS[s])}</option>
                                ))}
                            </select>
                        </div>
                    </div>
                    <div style={{ flex: 1, background: C.white, borderRadius: 12, border: `1px solid ${C.border}`, overflowY: 'auto', minHeight: 0 }}>
                        {loading ? (
                            <div style={{ padding: 48, textAlign: 'center', color: C.mutedLight, fontSize: 13 }}>{t('loading')}</div>
                        ) : (
                            <RunsTable runs={runs} currency={currency} onOpen={openRun} t={t} isMobile={isMobile} />
                        )}
                    </div>
                </div>
            </div>

            {showCreate && (
                <CreateRunModal
                    onSave={handleCreate}
                    onClose={() => { setShowCreate(false); setCreateError(''); }}
                    saving={creating}
                    serverError={createError}
                    t={t}
                />
            )}
        </PortalLayout>
    );
};

export default PortalPayRunPage;
